import { Fragment, useCallback, useEffect, useState } from "react";
import { Sparkles, Trash2 } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";

import { mapLeadService, parseAnalysis, type LeadPage } from "../../api/mapLeadService";
import { AnalysisCard } from "./AnalysisCard";
import { errorToast } from "@/shared/lib/apiErrors";
import { usePersona } from "@/shared/brand/persona";

/** 「我的线索」:首版 = 列表 + 删除,无跟进状态机(定稿拍板①:平台首个线索池,止步于列表)。 */
export function MyLeadsTab() {
  const persona = usePersona();
  const [data, setData] = useState<LeadPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const resp = await mapLeadService.leads(p, 20);
      setData(resp);
      setPage(resp.page);
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "加载线索失败" }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(1);
  }, [load]);

  const remove = useCallback(
    async (id: number, name: string) => {
      try {
        await mapLeadService.deleteLead(id);
        toast({ title: "已删除线索", description: name });
        void load(page);
      } catch (e) {
        toast(errorToast(e, { fallbackTitle: "删除失败" }));
      }
    },
    [load, page]
  );

  if (!loading && data && data.items.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-10 text-center">
        <p className="text-sm font-medium">线索池还是空的</p>
        <p className="mt-1 text-xs text-muted-foreground">
          去「找客户」搜索潜在客户,点「加入线索」收进来
        </p>
      </div>
    );
  }

  const pageCount = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-4 py-2.5 text-sm font-semibold" data-testid="my-leads-total">
        共 <span className="tabular-nums">{data?.total ?? 0}</span> 条线索
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>企业名称</TableHead>
            <TableHead>电话</TableHead>
            <TableHead className="hidden md:table-cell">地址</TableHead>
            <TableHead className="hidden sm:table-cell">行业</TableHead>
            <TableHead className="w-20 text-right">评分</TableHead>
            <TableHead className="w-28">加入时间</TableHead>
            <TableHead className="w-16" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data?.items ?? []).map((l) => {
            const analysis = parseAnalysis(l.aiAnalysis);
            return (
              <Fragment key={l.id}>
                <TableRow>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell className="tabular-nums">{l.phone ?? "—"}</TableCell>
                  <TableCell className="hidden max-w-[280px] truncate text-muted-foreground md:table-cell">
                    {l.address ?? "—"}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{l.industry ?? "—"}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {l.ruleScore ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums text-muted-foreground">
                    {l.createdAt?.slice(0, 10) ?? "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-0.5">
                      {analysis && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-1.5 text-primary"
                          aria-label={`查看 ${l.name} 的${persona.title}分析`}
                          onClick={() => setOpenId(openId === l.id ? null : l.id)}
                        >
                          <Sparkles className="mr-0.5 h-3.5 w-3.5" />
                          {openId === l.id ? "收起" : "分析"}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        aria-label={`删除 ${l.name}`}
                        onClick={() => remove(l.id, l.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {analysis && openId === l.id && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/30">
                      <div className="max-w-2xl py-1">
                        <AnalysisCard analysis={analysis} />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-1 border-t px-4 py-2 text-xs">
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1)}
          >
            上一页
          </Button>
          <span className="px-2 tabular-nums text-muted-foreground">
            {page} / {pageCount}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            disabled={page >= pageCount || loading}
            onClick={() => load(page + 1)}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}
